<template>
  <v-expansion-panels accordion class="affiliate-expansion my-2">
    <v-expansion-panel
      :append-icon="$vuetify.breakpoint.smAndDown ? '' : '$expand'"
      class="affiliate-card"
    >
      <v-expansion-panel-header class="pa-4">
        <v-row align="center" class="ma-0">
          <!-- Avatar -->
          <v-col
            cols="auto"
            class="d-flex justify-center align-center pa-0 avatar-col"
          >
            <v-tooltip
              top
              color="#f44336"
              v-if="enableAffiliatePayment && affiliate.isPendingPayment"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  icon
                  color="red"
                  :to="redirectPaymentUrl()"
                  v-bind="attrs"
                  v-on="on"
                  class="pending-payment-btn"
                >
                  <v-icon color="red"> mdi-alert-octagon </v-icon>
                </v-btn>
              </template>
              <span>
                Continua el proceso de pago de tu afiliado para activar su
                subscripción
              </span>
            </v-tooltip>
            <v-avatar size="100" class="mr-3 align-self-center">
              <v-img
                v-if="
                  affiliate.profilePicture &&
                  affiliate.profilePicture !== 'NOT_ASSIGNED' &&
                  affiliate.profilePicture !== 'N/D'
                "
                :src="affiliate.profilePicture"
              ></v-img>
              <v-img
                v-else
                src="https://media.istockphoto.com/id/1337144146/vector/default-avatar-profile-icon-vector.jpg?b=1&s=170667a&w=0&k=20&c=-qQGlKM8OQsSJCEkHnqS9FI94VRTkZ-7tg0K0u02XL0="
              ></v-img>
            </v-avatar>
          </v-col>

          <!-- Datos principales -->
          <v-col cols="12" sm="3" class="data-col">
            <div class="d-flex flex-column">
              <span
                class="wc-body-2 text-uppercase text-primary-variant font-weight-black text-truncate member-name"
              >
                {{ fullName }}
              </span>
              <span
                class="wc-caption-1 text-disabled text-truncate member-email"
              >
                {{ affiliate.email || "N/D" }}
              </span>
            </div>
          </v-col>

          <!-- Campos distribuidos simétricamente -->
          <v-col cols="12" sm="4" class="fields-col">
            <div class="sym-fields d-flex flex-row">
              <div class="sym-col d-flex flex-column pr-2">
                <div
                  v-for="(field, index) in leftFields"
                  :key="'left-' + index"
                  class="extra-field d-flex flex-column"
                >
                  <template v-if="field">
                    <span class="field-label">{{ field.name }}</span>
                    <span class="field-value text-truncate">{{
                      field.value
                    }}</span>
                  </template>
                </div>
              </div>
              <div class="sym-col d-flex flex-column pl-2">
                <div
                  v-for="(field, index) in rightFields"
                  :key="'right-' + index"
                  class="extra-field d-flex flex-column"
                >
                  <template v-if="field">
                    <span class="field-label">{{ field.name }}</span>
                    <span class="field-value text-truncate">{{
                      field.value
                    }}</span>
                  </template>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Estado -->
          <v-col
            cols="6"
            sm="2"
            class="d-flex align-center justify-center status-col"
          >
            <v-chip
              :color="statusInfo.color"
              class="rounded-pill font-weight-bold px-4 status-chip"
            >
              {{ statusInfo.status }}
            </v-chip>
          </v-col>

          <!-- Acciones -->
          <v-col
            cols="6"
            sm="auto"
            class="d-flex align-center justify-end ml-auto pr-2 actions-col"
          >
            <v-btn
              icon
              :color="color.primaryVariant"
              @click.stop="$emit('edit', affiliate)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              class="ml-3"
              :color="color.primaryVariant"
              @click.stop="$emit('delete', affiliate)"
            >
              <v-icon>mdi-trash-can</v-icon>
            </v-btn>
            <v-btn
              v-if="enableAffiliatePayment"
              icon
              class="ml-1"
              :color="color.primaryVariant"
              :disabled="!affiliate.isPendingPayment"
              :to="redirectPaymentUrl()"
              @click.stop
            >
              <v-icon>mdi-currency-usd</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-row v-if="memberFieldGroups.length" class="pa-2 pl-7">
          <template v-for="group in memberFieldGroups">
            <v-col
              v-if="group.title"
              cols="12"
              :key="'section-' + group._id"
              class="pb-0 pt-3"
            >
              <p class="subtitle-1 font-weight-bold mb-1 primary--text">
                {{ group.title }}
              </p>
              <v-divider></v-divider>
            </v-col>
            <v-col
              :cols="$vuetify.breakpoint.smAndDown ? 12 : 4"
              class="py-1"
              v-for="field in group.fields"
              :key="field.formattedName"
            >
              <p>
                <span
                  ><strong>{{ field.name }}</strong
                  ><br />{{ field.value }}</span
                >
              </p>
            </v-col>
          </template>
        </v-row>
        <v-row v-else class="pa-2 pl-7">
          <v-col cols="12">
            <p class="text-disabled mb-0">Sin campos adicionales.</p>
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapState } from "vuex";
import { colors } from "@/constants/colors.js";
import {
  homologateStatusWithColor,
  isActivePaymentNationalH,
  isActivePaymentLocalH,
} from "@/common/hook/useHelper.js";
import {
  groupFieldsBySection,
  buildMemberDataFromDefs,
  sortFieldsByOrder,
} from "@/utils";

export default {
  props: {
    affiliate: {
      type: Object,
      default: () => ({}),
    },
    memberFields: {
      type: Array,
      default: () => [],
    },
    orgFormSections: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("membershipProfile", ["userMembership"]),
    fullName() {
      const name = this.affiliate.name || "";
      const last = this.affiliate.firstLastname || "";
      return `${name} ${last}`.trim() || "N/D";
    },
    formattedCreatedAt() {
      if (!this.affiliate.createdAt) return "N/D";
      return this.moment(this.affiliate.createdAt).format("YYYY-MM-DD");
    },
    statusInfo() {
      return homologateStatusWithColor(this.affiliate.validationStatus);
    },
    nonEmptyMemberData() {
      if (!this.affiliate.memberData || !this.affiliate.memberData.length)
        return [];
      // Order the submember's stored values by the live field `order` so the
      // collapsed card preview follows the org's registration form ordering.
      // buildMemberDataFromDefs materializes rows for fields added by the
      // admin AFTER the submember was created, so they show up as empty
      // rows (which the filter below then drops from the header preview).
      const ordered = sortFieldsByOrder(
        buildMemberDataFromDefs(this.affiliate.memberData, this.memberFields)
      );
      return ordered.filter(
        (f) =>
          f.value !== null &&
          f.value !== undefined &&
          f.value !== "" &&
          !(Array.isArray(f.value) && f.value.length === 0)
      );
    },
    displayFields() {
      const fields = [{ name: "F. Inicio", value: this.formattedCreatedAt }];
      if (this.nonEmptyMemberData.length) {
        this.nonEmptyMemberData.forEach((f) => {
          fields.push({ name: f.name, value: this.formatFieldValue(f.value) });
        });
      } else {
        fields.push({ name: "Cargo", value: this.affiliate.position || "N/D" });
      }
      return fields;
    },
    leftFields() {
      const capped = this.displayFields.slice(0, 6);
      const half = Math.ceil(capped.length / 2);
      const fields = capped.slice(0, half);
      while (fields.length < 3) fields.push(null);
      return fields;
    },
    rightFields() {
      const capped = this.displayFields.slice(0, 6);
      const half = Math.ceil(capped.length / 2);
      const fields = capped.slice(half);
      while (fields.length < 3) fields.push(null);
      return fields;
    },
    enableAffiliatePayment() {
      const { affiliationType: nationalAffiliation } =
        this.user?.affiliation?.national?.details[0] || {};
      return !!nationalAffiliation?.sublevelPriceConfig?.active;
    },
    memberFieldGroups() {
      return this.getMemberFieldsBySection({
        memberData: this.affiliate.memberData,
      });
    },
  },
  methods: {
    homologateStatusWithColor,
    isActivePaymentNationalH,
    isActivePaymentLocalH,
    retrieveMemberFields({ memberData = [] }) {
      if (!memberData || memberData.length <= 0) return [];
      // Materialize one row per live field definition (merged with the
      // stored value when present) so the expanded view shows fields the
      // admin added AFTER the submember was created, even though their
      // stored `memberData` does not yet have an entry for them.
      return buildMemberDataFromDefs(memberData, this.memberFields).map(
        (field) => {
          return {
            formattedName: field.formattedName,
            name: field.name,
            value: field?.value || "N/D",
            order: field.order,
            section: field.section || null,
          };
        }
      );
    },
    getMemberFieldsBySection({ memberData }) {
      const fields = this.retrieveMemberFields({ memberData });
      const sectionMap = {};
      (this.memberFields || []).forEach((f) => {
        sectionMap[f.formattedName] = f.section || null;
      });
      const enriched = fields.map((f) => ({
        ...f,
        section: f.section || sectionMap[f.formattedName] || null,
      }));
      return groupFieldsBySection(this.orgFormSections, enriched).filter(
        (group) => group.fields.length > 0
      );
    },
    formatFieldValue(value) {
      if (Array.isArray(value)) return value.join(", ");
      if (value === null || value === undefined || value === "") return "N/D";
      return value;
    },
    redirectPaymentUrl() {
      if (
        this.userMembership &&
        this.userMembership?.organization &&
        this.userMembership.organization?.id
      ) {
        return `/member/newAffiliate/2/${this.affiliate._id}/${this.userMembership.organization.id}`;
      }
      return `/member/newAffiliate/2/${this.affiliate._id}`;
    },
  },
  async mounted() {
    this.affiliation = this.affiliate.affiliation;
  },
  watch: {
    affiliation(val) {
      if (val) {
        this.urlNextPayment = `/member/payment/${this.affiliate.affiliation.local._id}/${this.affiliate._id}`;

        this.isActivePaymentNational = this.isActivePaymentNationalH(val);
        this.isActivePaymentLocal = this.isActivePaymentLocalH(val);

        switch (this.user.headOffice.paymentType) {
          case "NATIONAL":
            this.isDisabledPayment = !this.isActivePaymentNational;
            break;
          case "LOCAL":
            this.isDisabledPayment = !this.isActivePaymentLocal;
            break;
          case "BOTH":
            this.isDisabledPayment =
              !this.isActivePaymentLocal && !this.isActivePaymentNational;
            break;
          default:
            this.isDisabledPayment = true;
        }

        if (this.isActivePaymentNational) {
          this.typePayment = "nacional";
          this.urlNextPayment = `/member/payment/${this.affiliation.national._id}/${this.affiliate._id}`;
        }
      }
    },
  },
  data() {
    return {
      color: colors,
      isActivePaymentNational: false,
      isActivePaymentLocal: false,
      isDisabledPayment: true,
      affiliation: {},
      urlNextPayment: "",
      typePayment: "",
    };
  },
};
</script>

<style scoped>
.affiliate-expansion {
  width: 100%;
}
.affiliate-card {
  width: 100%;
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.affiliate-card >>> .v-expansion-panel-header {
  min-height: auto;
  padding: 16px;
}
.affiliate-card >>> .v-expansion-panel-header--active {
  min-height: auto;
}
.member-name {
  font-size: 0.95rem;
  line-height: 1.2;
}
.member-email {
  font-size: 0.8rem;
}
.field-label {
  font-size: 0.7rem;
  color: #8c929e;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.field-value {
  font-size: 0.85rem;
  color: #1b273e;
  font-weight: 500;
  max-width: 100%;
  display: inline-block;
  line-height: 1.4;
}
.extra-field {
  display: flex;
  flex-direction: column;
  min-height: 2.5rem;
  max-height: 2.5rem;
  overflow: hidden;
  margin-bottom: 2px;
}
.sym-fields {
  display: flex;
  flex-direction: row;
}
.sym-col {
  flex: 1 1 0;
  min-width: 0;
}
.status-chip {
  font-size: 0.95rem !important;
  height: 40px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  vertical-align: middle !important;
}
.pending-payment-btn {
  position: absolute;
  top: 4px;
  left: 0;
}
.avatar-col {
  position: relative;
}
@media (max-width: 600px) {
  .affiliate-card .row {
    flex-direction: column;
    align-items: flex-start;
  }
  .data-col,
  .fields-col,
  .status-col,
  .actions-col {
    width: 100%;
    justify-content: flex-start !important;
  }
  .sym-fields {
    flex-direction: column;
  }
}
</style>
